import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  Loader2,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Star,
  Upload,
} from 'lucide-react';
import { client } from './AuthContext';

const TABS = [
  { slug: 'contoh-aplikasi', title: 'Contoh Aplikasi' },
  { slug: 'panduan-edukasi', title: 'Panduan & Edukasi' },
];

const GROUPS = [
  { slug: 'solar-panel', title: 'Sistem Solar Panel' },
  { slug: 'solar-light', title: 'Sistem Solar Lampu Penerangan Jalan' },
  { slug: 'telecom', title: 'Sistem Solar untuk Telecom & Surveillance' },
  { slug: 'ups', title: 'Sistem UPS' },
];

const BLOCK_TYPES = [
  { v: 'p', label: '+ Paragraf' },
  { v: 'h2', label: '+ Heading' },
  { v: 'ul', label: '+ List Poin' },
  { v: 'image', label: '+ Gambar' },
  { v: 'quote', label: '+ Kutipan' },
  { v: 'cta', label: '+ Tombol CTA' },
];

const BLOCK_LABEL = {
  p: 'Paragraf',
  h2: 'Heading',
  ul: 'List Poin',
  image: 'Gambar',
  quote: 'Kutipan',
  cta: 'Tombol CTA',
};

const emptyArticle = {
  slug: '',
  title: '',
  tag: 'ARTIKEL',
  category: 'edukasi',
  tab: 'panduan-edukasi',
  group: 'solar-panel',
  excerpt: '',
  image: '',
  author: 'Tim PowerX',
  date: new Date().toISOString().slice(0, 10),
  read: '5 menit',
  featured: false,
  body: [],
};

const newBlock = (type) =>
  ({
    p: { type: 'p', text: '' },
    h2: { type: 'h2', text: '' },
    ul: { type: 'ul', items: [''] },
    image: { type: 'image', src: '', alt: '' },
    quote: { type: 'quote', text: '' },
    cta: { type: 'cta', text: '', to: '/contact' },
  }[type]);

const inputCls =
  'mt-1 w-full bg-neutral-50 border border-neutral-200 focus:border-neutral-900 outline-none rounded-lg px-3 py-2.5 text-sm';

const Row = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
      {label}
    </label>
    {children}
  </div>
);

function BlockEditor({ blocks, onChange, onUploadImage, uploadingIdx }) {
  const set = (i, patch) =>
    onChange(blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const arr = [...blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const rm = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const add = (type) => onChange([...blocks, newBlock(type)]);

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
        Isi Artikel ({blocks.length} blok)
      </label>
      <div className="mt-2 space-y-3">
        {blocks.map((b, i) => (
          <div
            key={i}
            data-testid={`article-block-${i}`}
            className="border border-neutral-200 rounded-xl p-4 bg-white"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold tracking-wider uppercase bg-neutral-900 text-white px-2 py-1 rounded">
                {i + 1} · {BLOCK_LABEL[b.type] || b.type}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                  className="p-1.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" aria-label="naik">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === blocks.length - 1}
                  className="p-1.5 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" aria-label="turun">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => rm(i)}
                  data-testid={`block-delete-${i}`}
                  className="p-1.5 text-neutral-400 hover:text-red-600" aria-label="hapus blok">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {(b.type === 'p' || b.type === 'quote') && (
              <textarea rows={3} className={`${inputCls} resize-none`}
                placeholder={b.type === 'quote' ? 'Teks kutipan...' : 'Tulis paragraf...'}
                value={b.text || ''}
                onChange={(e) => set(i, { text: e.target.value })} />
            )}
            {b.type === 'h2' && (
              <input className={inputCls} placeholder="Judul sub-bab..."
                value={b.text || ''}
                onChange={(e) => set(i, { text: e.target.value })} />
            )}
            {b.type === 'ul' && (
              <textarea rows={4} className={`${inputCls} resize-none`}
                placeholder={'Satu poin per baris:\nPoin pertama\nPoin kedua'}
                value={Array.isArray(b.items) ? b.items.join('\n') : String(b.items || '')}
                onChange={(e) => set(i, { items: e.target.value.split('\n') })} />
            )}
            {b.type === 'image' && (
              <div className="space-y-2">
                <div className="flex gap-2 items-center">
                  <input className={`${inputCls} flex-1`} placeholder="URL gambar (https://...)"
                    value={b.src || ''}
                    data-testid={`block-image-url-${i}`}
                    onChange={(e) => set(i, { src: e.target.value })} />
                  <label className="mt-1 cursor-pointer inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap">
                    {uploadingIdx === i ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Mengunggah...
                      </>
                    ) : (
                      <>
                        <Upload className="w-3.5 h-3.5" /> Upload File
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={uploadingIdx !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUploadImage(i, f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <input className={inputCls} placeholder="Caption / alt text (opsional)"
                  value={b.alt || ''}
                  onChange={(e) => set(i, { alt: e.target.value })} />
                {b.src && (
                  <div className="mt-2 p-2 bg-neutral-50 rounded-lg border border-neutral-200 inline-block">
                    <img src={b.src} alt="" className="max-h-48 rounded object-contain" />
                  </div>
                )}
              </div>
            )}
            {b.type === 'cta' && (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Teks tombol"
                  value={b.text || ''}
                  onChange={(e) => set(i, { text: e.target.value })} />
                <input className={inputCls} placeholder="Link tujuan (mis. /contact)"
                  value={b.to || ''}
                  onChange={(e) => set(i, { to: e.target.value })} />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {BLOCK_TYPES.map((t) => (
          <button key={t.v} type="button" onClick={() => add(t.v)}
            data-testid={`add-block-${t.v}`}
            className="text-xs font-semibold text-px-red border border-red-200 hover:bg-red-50 rounded-full px-3 py-1.5">
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AdminArticles() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingBlockIdx, setUploadingBlockIdx] = useState(null);

  const load = () =>
    client.get('/api/admin/articles').then((r) => setList(r.data));

  useEffect(() => {
    load().catch(() => setList([]));
  }, []);

  const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// Resize gambar besar (foto HP bisa 5MB+) sebelum upload/scan AI agar tidak gagal di proxy
const downscaleImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const MAX = 1600;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      if (scale >= 1 && file.size < 900 * 1024) {
        URL.revokeObjectURL(url);
        resolve(file);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) =>
          resolve(
            blob
              ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
              : file
          ),
        'image/jpeg',
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });

const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'articles');
    const { data } = await client.post('/api/admin/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  };

  const [generating, setGenerating] = useState(false);

  const aiDraft = async (file) => {
    if (!file) return;
    setGenerating(true);
    try {
      const prepared = await downscaleImage(file);
      const b64 = await fileToBase64(prepared);
      // upload gambar ke storage dulu untuk cover
      const url = await uploadFile(prepared);
      const { data } = await client.post('/api/admin/articles/ai-draft', { image_base64: b64 });
      const aiBody = Array.isArray(data?.body) ? data.body : [];
      setEditing((e) => ({
        ...e,
        title: typeof data?.title === 'string' && data.title ? data.title : e.title,
        excerpt: typeof data?.excerpt === 'string' && data.excerpt ? data.excerpt : e.excerpt,
        image: url,
        body: [
          { type: 'image', src: url, alt: typeof data?.title === 'string' && data.title ? data.title : 'Diagram sistem' },
          ...aiBody,
          ...(Array.isArray(e.body) ? e.body : []),
        ],
        estimate: data?.estimate && typeof data.estimate === 'object' ? data.estimate : null,
      }));
      toast.success('Draft artikel + estimasi berhasil dibuat dari gambar');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadFile(file);
      setEditing((e) => ({ ...e, image: url }));
      toast.success('Gambar sampul berhasil diunggah');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload gambar gagal');
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadBlockImage = async (idx, file) => {
    if (!file) return;
    setUploadingBlockIdx(idx);
    try {
      const url = await uploadFile(file);
      setEditing((e) => {
        const nextBody = e.body.map((b, i) =>
          i === idx ? { ...b, src: url } : b
        );
        return { ...e, body: nextBody };
      });
      toast.success('Gambar artikel berhasil diunggah');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload gambar gagal');
    } finally {
      setUploadingBlockIdx(null);
    }
  };

  const startNew = () => setEditing({ ...emptyArticle, _isNew: true });
  const startEdit = (a) =>
    setEditing({ ...emptyArticle, ...a, body: a.body || [], _origImage: a.image || '' });

  const remove = async (a) => {
    if (!window.confirm(`Hapus artikel "${a.title}"?`)) return;
    try {
      await client.delete(`/api/admin/articles/${a.slug}`);
      toast.success('Artikel dihapus');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing };
    delete payload._isNew;
    delete payload.id;
    if (!payload.slug.trim()) {
      payload.slug = payload.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }
    payload.body = (payload.body || []).map((b) =>
      b.type === 'ul'
        ? { ...b, items: (b.items || []).filter((t) => t.trim()) }
        : b
    );
    // Jika gambar sampul diganti, blok gambar di isi artikel yang masih
    // memakai gambar lama ikut terganti otomatis
    let synced = 0;
    if (editing._origImage && payload.image && payload.image !== editing._origImage) {
      payload.body = payload.body.map((b) => {
        if (b.type === 'image' && b.src === editing._origImage) {
          synced += 1;
          return { ...b, src: payload.image };
        }
        return b;
      });
    }
    delete payload._origImage;
    try {
      if (editing._isNew) {
        await client.post('/api/admin/articles', payload);
      } else {
        await client.put(`/api/admin/articles/${payload.slug}`, payload);
      }
      toast.success('Artikel tersimpan');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  if (list === null)
    return <p className="text-sm text-neutral-500">Memuat artikel...</p>;

  // ---------- FORM MODE ----------
  if (editing) {
    return (
      <div data-testid="admin-article-form">
        <button
          onClick={() => setEditing(null)}
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-6"
          data-testid="article-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Kembali ke daftar
        </button>
        <h1 className="font-display text-3xl font-bold text-neutral-900 mb-8">
          {editing._isNew ? 'Tulis Artikel Baru' : `Edit: ${editing.title}`}
        </h1>

        {editing._isNew && (
          <label className="mb-5 inline-flex items-center gap-2 cursor-pointer bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold px-5 py-3 rounded-full">
            {generating ? (<><Loader2 className="w-4 h-4 animate-spin" /> AI sedang membaca gambar...</>) : (<>✨ Generate Draft dari Gambar Skema</>)}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={generating}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) aiDraft(f); e.target.value=''; }} />
          </label>
        )}
        <form onSubmit={save} className="space-y-6">
          <div className="bg-white rounded-2xl border border-neutral-200 p-8 space-y-5">
            <Row label="Judul Artikel *">
              <input required className={inputCls} value={editing.title}
                data-testid="article-form-title"
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </Row>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Slug (URL)">
                <input className={inputCls} value={editing.slug}
                  placeholder="kosongkan = otomatis dari judul"
                  disabled={!editing._isNew}
                  data-testid="article-form-slug"
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </Row>
              <Row label="Tag (label merah)">
                <input className={inputCls} value={editing.tag}
                  onChange={(e) => setEditing({ ...editing, tag: e.target.value })} />
              </Row>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Row label="Tab Halaman Artikel">
                <select className={inputCls} value={editing.tab}
                  data-testid="article-form-tab"
                  onChange={(e) => setEditing({ ...editing, tab: e.target.value, category: e.target.value })}>
                  {TABS.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.title}</option>
                  ))}
                </select>
              </Row>
              <Row label="Grup Sistem">
                <select className={inputCls} value={editing.group}
                  data-testid="article-form-group"
                  onChange={(e) => setEditing({ ...editing, group: e.target.value })}>
                  {GROUPS.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.title}</option>
                  ))}
                </select>
              </Row>
              <Row label="Tanggal">
                <input type="date" className={inputCls} value={editing.date}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
              </Row>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Row label="Penulis">
                <input className={inputCls} value={editing.author}
                  onChange={(e) => setEditing({ ...editing, author: e.target.value })} />
              </Row>
              <Row label="Estimasi Baca">
                <input className={inputCls} value={editing.read}
                  onChange={(e) => setEditing({ ...editing, read: e.target.value })} />
              </Row>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
                  <input type="checkbox" checked={editing.featured}
                    data-testid="article-form-featured"
                    onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                    className="w-4 h-4 accent-red-600" />
                  Tampilkan sebagai Featured
                </label>
              </div>
            </div>
            <Row label="Ringkasan (excerpt)">
              <textarea rows={2} className={`${inputCls} resize-none`}
                value={editing.excerpt}
                data-testid="article-form-excerpt"
                onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
            </Row>
            <Row label="Gambar Sampul (Cover)">
              <div className="flex gap-2 items-center mt-1">
                <input className={`${inputCls} flex-1 !mt-0`} value={editing.image}
                  data-testid="article-form-image"
                  placeholder="https://... atau klik tombol Upload"
                  onChange={(e) => setEditing({ ...editing, image: e.target.value })} />
                <label className="cursor-pointer inline-flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap">
                  {uploadingCover ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Mengunggah...
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" /> Upload File
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    disabled={uploadingCover}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadCover(f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {editing.image && (
                <div className="mt-2 p-2 bg-neutral-50 rounded-lg border border-neutral-200 inline-block">
                  <img src={editing.image} alt="" className="max-h-40 rounded object-contain" />
                </div>
              )}
            </Row>
          </div>

          <div className="bg-white rounded-2xl border border-neutral-200 p-8">
            <BlockEditor
              blocks={editing.body}
              uploadingIdx={uploadingBlockIdx}
              onUploadImage={uploadBlockImage}
              onChange={(body) => setEditing({ ...editing, body })}
            />
          </div>

          <button type="submit" disabled={saving}
            data-testid="article-save-btn"
            className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-colors">
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...</>
            ) : (
              <><Save className="w-4 h-4" /> Simpan Artikel</>
            )}
          </button>
        </form>
      </div>
    );
  }

  // ---------- LIST MODE ----------
  return (
    <div data-testid="admin-articles-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
            KONTEN
          </p>
          <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">
            News & Artikel
          </h1>
          <p className="text-neutral-600">
            Kelola artikel yang tampil di halaman /articles dan bagian Home.
          </p>
        </div>
        <button onClick={startNew} data-testid="article-new-btn"
          className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 text-white font-semibold px-5 py-3 rounded-full text-sm">
          <Plus className="w-4 h-4" /> Tulis Artikel Baru
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-white">
            <tr>
              <th className="px-4 py-3 text-left font-semibold w-16">Foto</th>
              <th className="px-4 py-3 text-left font-semibold">Judul</th>
              <th className="px-4 py-3 text-left font-semibold">Kategori</th>
              <th className="px-4 py-3 text-left font-semibold">Tanggal</th>
              <th className="px-4 py-3 text-center font-semibold">Featured</th>
              <th className="px-4 py-3 text-right font-semibold w-28">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.map((a, i) => (
              <tr key={a.slug} data-testid={`article-row-${a.slug}`}
                className={i % 2 ? 'bg-neutral-50' : 'bg-white'}>
                <td className="px-4 py-2">
                  {a.image ? (
                    <img src={a.image} alt="" className="w-12 h-12 object-cover rounded-lg border border-neutral-200" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg border border-dashed border-neutral-200 bg-neutral-50" />
                  )}
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-neutral-900 leading-snug">{a.title}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">/articles/{a.slug}</p>
                </td>
                <td className="px-4 py-3 text-neutral-600">{a.tag}</td>
                <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{a.date}</td>
                <td className="px-4 py-3 text-center">
                  {a.featured && <Star className="w-4 h-4 text-amber-500 fill-amber-500 inline" />}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(a)}
                    data-testid={`article-edit-${a.slug}`}
                    className="p-2 text-neutral-400 hover:text-neutral-900" aria-label="edit">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(a)}
                    data-testid={`article-delete-${a.slug}`}
                    className="p-2 text-neutral-400 hover:text-red-600" aria-label="hapus">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-neutral-400">
                  Belum ada artikel.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
